<template>
    <div class="card mt-4 px-0">
        <div class="card-header px-4">
            <div class="row align-items-center">
                <div class="col-md-6 col-12">
                   <h3 class="mb-0">Resources</h3> 
                </div>
                <div class="col-md-6 col-12 d-flex justify-content-end align-items-center gap-4">
                    <span class="text-muted" v-if="client.last_scraped_at">
                        Last Scrape: {{ datetime(client.last_scraped_at) }}
                    </span>
                    <button 
                        v-if="canEdit('settings')"
                        class="btn btn-primary" 
                        @click="triggerScrape" 
                        :disabled="scraping"
                        data-bs-toggle="tooltip"
                        title="Fetch latest content from news and resource URLs"
                    >
                        <span v-if="scraping" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        <img v-else src="/img/icons/avid-icons/refresh.svg" alt="Refresh" class="icon me-2">
                        {{ scraping ? 'Scraping...' : 'Scrape Latest Content' }}
                    </button>
                </div>
            </div>
            <p>Here's the research that powers your Playbooks, sourced from publicly available information based upon the URLs and terms specified above. It updates automatically each week, or you can upload your own brand materials, news, or stories to keep it even more current.</p>

            <!-- Tab Navigation -->
            <div class="row">
                <div class="col-md-6 col-12">
                    <nav class="navbar navbar-expand tertiary-tab-bar">
                        <ul class="navbar-nav">
                            <li class="nav-item">
                                <a :class="['nav-link d-flex flex-nowrap tertiary-tab', {'tertiary-tab-active': selectedUsage === 'news'}]" @click="selectUsage('news')" href="#">
                                    <span>News</span>
                                </a>
                            </li>
                            <li class="nav-item">
                                <a :class="['nav-link d-flex flex-nowrap tertiary-tab', {'tertiary-tab-active': selectedUsage === 'stories'}]" @click="selectUsage('stories')" href="#">
                                    <span>Stories</span>
                                </a>
                            </li>
                            <li class="nav-item">
                                <a :class="['nav-link d-flex flex-nowrap tertiary-tab', {'tertiary-tab-active': selectedUsage === 'brand'}]" @click="selectUsage('brand')" href="#">
                                    <span>Brand</span>
                                </a>
                            </li>
                        </ul>
                    </nav>
                </div>
                <div class="col-md-6 col-12 text-end mt-2 mt-md-0">
                    <div class="d-sm-flex d-block justify-content-end gap-2">
                        <div class="position-relative">
                            <input 
                                type="text" 
                                class="search-box form-control me-1 py-2" 
                                placeholder="Search by name..." 
                                aria-label="Search by name..." 
                                v-model="search_key" 
                                @input="debouncedSearch"
                            >
                            <i class="fa fa-search search-placeholder-icon" v-if="search_key === ''"></i>
                        </div>
                        <button 
                                v-if="canEdit('settings')"
                                class="btn btn-secondary mt-2 mt-md-0" 
                                @click="openUploadModal" 
                                :disabled="uploading || processingUrl" 
                                data-bs-toggle="tooltip"
                                :title="'Upload a ' + propercase(selectedUsage) + '  or link to a resource'"
                            >
                                <img src="/img/icons/avid-icons/upload.svg" alt="Upload" class="icon me-2">
                                <span v-if="selectedUsage=='brand'"> Upload a {{ propercase(selectedUsage) }} Guideline</span>
                                <span v-else> Upload {{ propercase(selectedUsage) }} </span>
                        </button>
                    </div>
                </div>
            </div>            
        </div>
        <div class="card-body px-0 py-2">
            <!-- Hidden file input -->
            <input type="file" class="d-none" @change="handleFileSelect" accept=".css,.doc,.docx,.go,.html,.java,.js,.json,.md,.pdf,.pptx,.tex,.ts,.txt" ref="fileInput" aria-label="File upload">
            
            <!-- Vectors Table -->
            <div v-if="vectors.length > 0">
                <div class="table-card">
                    <div class="card-body p-0">
                        <div class="table-responsive">
                            <table class="table table-with-pagination mb-0">
                                <thead>
                                    <tr class="table-header-row">
                                        <th @click="sortByKey('name')" class="align-middle d-table-cell ps-3">
                                            <div class="d-flex flex-row">
                                                Name
                                                <div class="d-flex flex-column">
                                                    <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="(sort_key=='name' && sort_order=='asc')? 'sort-key-active' :''" alt="Sort ascending">
                                                    <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="(sort_key=='name' && sort_order=='des')? 'sort-key-active' :''" alt="Sort descending">
                                                </div>
                                            </div>
                                        </th>
                                        <th @click="sortByKey('data_source')" class="align-middle d-none d-md-table-cell">
                                            <div class="d-flex flex-row">
                                                Source
                                                <div class="d-flex flex-column">
                                                    <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="(sort_key=='data_source' && sort_order=='asc')? 'sort-key-active' :''" alt="Sort ascending">
                                                    <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="(sort_key=='data_source' && sort_order=='des')? 'sort-key-active' :''" alt="Sort descending">
                                                </div>
                                            </div>
                                        </th>
                                        <th @click="sortByKey('created_at')" class="align-middle d-none d-md-table-cell">
                                            <div class="d-flex flex-row">
                                                Created
                                                <div class="d-flex flex-column">
                                                    <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="(sort_key=='created_at' && sort_order=='asc')? 'sort-key-active' :''" alt="Sort ascending">
                                                    <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="(sort_key=='created_at' && sort_order=='des')? 'sort-key-active' :''" alt="Sort descending">
                                                </div>
                                            </div>
                                        </th>
                                        <th class="align-middle d-table-cell pe-3 column-sort-disabled" width="1">
                                            <div class="d-flex flex-row">Actions</div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="vector in vectors" :key="vector.id">
                                        <td class="ps-3 align-middle d-table-cell py-3">
                                            <a 
                                                href="#" 
                                                @click.prevent="handleVectorClick(vector)" 
                                                class="text-decoration-none"
                                            >
                                                {{ vector.name }}
                                            </a>
                                        </td>
                                        <td class="align-middle d-none d-md-table-cell py-3">
                                            {{ getVisualDataSource(vector) }}
                                        </td>
                                        <td class="align-middle d-none d-md-table-cell py-3">
                                            {{ datetime(vector.created_at) }}
                                        </td>
                                        <td class="align-middle text-end d-table-cell pe-3 py-3" width="1">
                                            <button 
                                                v-if="canEdit('settings')"
                                                class="btn btn-warning btn-icon-only-sm" 
                                                @click="deleteVector(vector.id)" 
                                                data-bs-toggle="tooltip"
                                                title="Delete resource"
                                            >
                                                <img src="/img/icons/avid-icons/trash.svg" alt="Delete" class="icon">
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                                <tfoot class="table-border-bottom-0">
                                    <tr>
                                        <td colspan="100%" class="py-0">
                                            <div class="table-pagination mb-0">
                                                <div class="table-pagination--group d-none d-md-flex">
                                                    <div class="position-relative">
                                                        <select class="form-select" name="num_per_page" id="num_per_page" v-model="num_per_page" @change="reloadPagination()" aria-label="Num Per Page">
                                                            <option value="5000">ALL</option>
                                                            <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                                                        </select>
                                                        <div class="select-display-text">
                                                            Showing {{ num_per_page === 5000 ? 'ALL' : num_per_page }}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div class="table-pagination--group d-none d-lg-flex">
                                                    {{ local_num_vectors == 0 ? '0' : item_interval }} of {{ local_num_vectors }}
                                                </div>

                                                <div class="table-pagination--group">
                                                    <button v-if="local_num_vectors/num_per_page>1" class="btn btn-secondary btn-icon-only-md me-1" @click="moveToPrevPage()" :disabled="page_index == 0" data-bs-toggle="tooltip" title="Previous page">
                                                        <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="Previous">
                                                    </button>

                                                    <div class="d-flex page-selector" v-if="local_num_vectors/num_per_page<=3">
                                                        <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" :class="page == page_index + 1 ? 'current-page':''" @click="moveToPage(page)">
                                                            {{ page }}
                                                        </button>
                                                    </div>
                                                    <div class="d-flex page-selector" v-else>
                                                        <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" :class="page == page_index + 1 ? 'current-page':''" @click="moveToPage(page)">
                                                            {{ page }}
                                                        </button>

                                                        <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                                            ...
                                                        </button>
                                                        <button v-if="page_index >= 2 && page_index< Math.ceil(local_num_vectors/num_per_page) -1" class="btn btn-none btn-icon-only-md current-page">
                                                            {{ page_index + 1 }}
                                                        </button>

                                                        <button v-if="page_index < local_num_vectors/num_per_page -2" class="btn btn-none btn-icon-only-md text-nowrap" disabled aria-hidden="true">
                                                            ...
                                                        </button>
                                                        <button class="btn btn-none btn-icon-only-md" :class="page_index == Math.ceil(local_num_vectors/num_per_page)-1 ? 'current-page':''" @click="moveToPage(Math.ceil(local_num_vectors/num_per_page))">
                                                            {{ Math.ceil(local_num_vectors/num_per_page) }}
                                                        </button>
                                                    </div>

                                                    <button v-if="page_count>=1" class="btn btn-secondary btn-icon-only-md ms-1" @click="moveToNextPage()" :disabled="page_index == Math.ceil(local_num_vectors/num_per_page) -1" data-bs-toggle="tooltip" title="Next page">
                                                        <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="Next">
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="text-muted fst-italic mx-4">No {{ selectedUsage }} resources uploaded yet.</div>
        </div>
    </div>

    <!-- Markdown Content Modal -->
    <div class="modal fade" id="markdownModal" tabindex="-1" aria-labelledby="markdownModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header border-bottom-0 pb-0">
                    <div class="w-100 position-relative">
                        <button type="button" class="btn btn-white position-absolute top-0 end-0 close-modal" data-bs-dismiss="modal" style="z-index: 10;" data-bs-toggle="tooltip" title="Close">
                            <img class="icon me-0" src="/img/icons/avid-icons/close.svg" alt=""> 
                        </button>
                        <h3 class="mt-2 pe-5"></h3>
                    </div>
                </div>
                <div class="modal-body">
                    <div v-if="loadingContent" class="text-center">
                        <div class="spinner-border" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="mt-2">Loading content...</p>
                    </div>
                    <div v-else-if="markdownContent" class="markdown-content">
                        <!-- Display mode -->
                        <div v-if="!isEditMode" v-html="formattedMarkdown"></div>
                        <!-- Edit mode -->
                        <div v-else class="edit-mode">
                            <div class="mb-3">
                                <label for="editedContent" class="form-label">Content</label>
                                <textarea 
                                    id="editedContent"
                                    class="form-control" 
                                    rows="15" 
                                    v-model="editedContent"
                                    placeholder="Enter markdown content..."
                                ></textarea>
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-muted">
                        <p>Unable to load content.</p>
                    </div>
                </div>
                <div class="modal-footer">
                    <div class="d-flex justify-content-end w-100">
                        <!-- Edit button (only show in display mode and for non-document vectors) -->
                        <button 
                            v-if="!isEditMode && currentVector && currentVector.data_source !== 'client_documents'"
                            type="button" 
                            class="btn btn-secondary me-2" 
                            @click="toggleEditMode"
                            data-bs-toggle="tooltip"
                            title="Edit content"
                        >
                            <img src="/img/icons/avid-icons/pencil.svg" alt="Edit" class="icon me-2">
                            Edit
                        </button>
                        <!-- Save button (only show in edit mode) -->
                        <button 
                            v-if="isEditMode"
                            type="button" 
                            class="btn btn-secondary me-2" 
                            @click="saveContent"
                            :disabled="saving"
                            data-bs-toggle="tooltip"
                            title="Save content"
                        >
                            <img src="/img/icons/avid-icons/save.svg" alt="Save" class="icon me-2" v-if="!saving">
                            <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
                            {{ saving ? 'Saving...' : 'Save' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Upload Modal - URL or File Upload -->
    <Teleport to="body">
        <div class="modal fade" id="resource-upload-modal" tabindex="-1" aria-labelledby="resourceUploadModalLabel" aria-hidden="true" data-bs-backdrop="static">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header border-bottom-0 pb-0">
                        <h5 class="modal-title" id="resourceUploadModalLabel">
                            <span v-if="selectedUsage=='brand'">Upload a {{ propercase(selectedUsage) }} Guideline</span>
                            <span v-else>Upload {{ propercase(selectedUsage) }}</span>
                        </h5>
                        <button type="button" class="btn btn-white close-modal" data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close" @click="closeUploadModal">
                            <img class="icon" src="/img/icons/avid-icons/close.svg" alt="">
                        </button>
                    </div>
                    <div class="modal-body">
                        <!-- URL Input Section -->
                        <div class="mb-4">
                            <label :for="'resource-url-' + selectedUsage" class="form-label">Enter a {{ propercase(selectedUsage) }} URL:</label>
                            <input 
                                type="url" 
                                :id="'resource-url-' + selectedUsage"
                                v-model="modalUrlValue"
                                class="form-control" 
                                placeholder="https://example.com/article"
                                :disabled="uploading || processingUrl || (modalFileValue !== null)"
                                @input="handleUrlInput"
                             aria-label="Enter a {{ propercase(selectedUsage) }} URL:">
                        </div>

                        <!-- Divider with OR text -->
                        <div class="d-flex align-items-center mb-3">
                            <hr class="flex-grow-1">
                            <span class="mx-3 text-muted">OR</span>
                            <hr class="flex-grow-1">
                        </div>

                        <!-- File Upload Section -->
                        <div>
                            <label class="form-label">Upload a file:</label>
                            <div 
                                ref="dropzone"
                                class="file-dropzone"
                                v-bind="getRootProps ? getRootProps() : {}"
                                :class="{ 'is-disabled': uploading || processingUrl || (modalUrlValue && modalUrlValue.trim().length > 0) }"
                            >
                                <input v-bind="getInputProps ? getInputProps() : {}" aria-label="Text input">
                                <div v-if="!isDragActive" class="dz-message">
                                    <img src="/img/icons/avid-icons/arrow-circle-up.svg" alt="">
                                    <div class="dropzone-title">Drag and drop or click to upload</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeUploadModal">Cancel</button>
                        <button 
                            type="button" 
                            class="btn btn-primary" 
                            @click="confirmUpload"
                            :disabled="uploading || processingUrl || (!modalUrlValue && !modalFileValue) || (modalUrlValue && !isValidUrl(modalUrlValue))"
                        >
                            <span v-if="uploading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                            <span v-if="processingUrl" class="spinner-border spinner-border-sm me-2" role="status"></span>
                            <img v-if="!uploading && !processingUrl && modalUrlValue" class="icon" src="/img/icons/avid-icons/plus.svg" alt="" >
                            <img v-if="!uploading && !processingUrl && !modalUrlValue" class="icon" src="/img/icons/avid-icons/upload.svg" alt="" >
                            {{ (uploading || processingUrl) ? 'Processing...' : (modalUrlValue ? 'Add URL' : 'Upload File') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script>
import { defineComponent, ref, onMounted } from 'vue';
import { useDropzone } from 'vue3-dropzone';
import permissionsMixin from '../../../mixins/permissions';
import Swal from 'sweetalert2';
import { useNotifications } from '../../../composables/useNotifications';
import debounce from 'lodash.debounce';
import filters from '../../../filters';

export default defineComponent({
    name: 'ResourcesSettings',
    mixins: [permissionsMixin],
    setup() {
        const { showNotification } = useNotifications();
        return {
            showNotification
        };
    },
    props: {
        user: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            // Brand resources data
            selectedUsage: 'news',
            vectors: [],
            uploadFile: null,
            uploading: false,
            // Search and sorting
            search_key: '',
            sort_key: 'created_at',
            sort_order: 'des',
            // Pagination
            num_per_page: 25,
            num_per_page_options: [5, 10, 15, 20, 25, 30, 50, 100],
            page_count: null,
            page_index: 0,
            page_index_array: [],
            item_interval: '',
            local_num_vectors: 0,
            // Modal data
            modalTitle: '',
            markdownContent: '',
            loadingContent: false,
            // Edit mode
            isEditMode: false,
            editedContent: '',
            currentVector: null,
            saving: false,
            // Echo channel for pusher
            echoChannel: null,
            // Scraping state
            scraping: false,
            // Modal state
            modalUrlValue: '',
            modalFileValue: null,
            processingUrl: false,
            // Dropzone state
            getRootProps: null,
            getInputProps: null,
            isDragActive: false,
            openDropzone: null,
        };
    },
    mounted() {
        // Initialize vectors for the default usage
        this.fetchVectors(this.selectedUsage);
        // Initialize debounced search
        this.debouncedSearch = debounce(this.searchVectors, 500);

        // Subscribe to Pusher events for client vectors
        if (window.Echo && this.client && this.client.id) {
            this.echoChannel = window.Echo.private('client.' + this.client.id)
                .listen('ClientVectorCreated', (e) => {
                    //Refresh list when a new vector is created
                    this.fetchVectors(this.selectedUsage);
                })
                .listen('ClientVectorUpdated', (e) => {
                    this.fetchVectors(this.selectedUsage);
                })
                .listen('ClientVectorDeleted', (e) => {
                    this.fetchVectors(this.selectedUsage);
                });
        }
        
        // Initialize dropzone
        this.initializeDropzone();
    },
    beforeUnmount() {
        // Unsubscribe from Echo events
        if (window.Echo && this.client && this.client.id) {
            window.Echo.private('client.' + this.client.id)
                .stopListening('ClientVectorCreated')
                .stopListening('ClientVectorUpdated')
                .stopListening('ClientVectorDeleted');
        }
        
        // Clean up upload modal if it exists
        const modalElement = document.getElementById('resource-upload-modal');
        if (modalElement) {
            const modal = window.bootstrap.Modal.getInstance(modalElement);
            if (modal) {
                modal.hide();
                modal.dispose();
            }
        }
    },
    methods: {
        getVisualDataSource(vector) {
            const dataSource = vector.data_source;
            if(dataSource == 'resource_urls' || dataSource == 'brave_api') {
                //Extract the domain from the source_url
                const domain = new URL(vector.source_url).hostname;
                return domain;
            }
            else if(dataSource == 'youtube')
                return 'YouTube';
            else if(dataSource == 'facebook')
                return 'Facebook';
            else if(vector.content_type == 'client_documents') {
                return 'Uploaded Document';
            }
            return this.propercase(dataSource);
        },
        selectUsage(usage) {
            this.selectedUsage = usage;
            this.fetchVectors(usage);
        },
        
        async fetchVectors(usage) {
            try {
                const params = new URLSearchParams({
                    usage: usage,
                    search: this.search_key || '',
                    sort_key: this.sort_key,
                    sort_order: this.sort_order,
                    page_size: this.num_per_page,
                    offset: this.num_per_page * this.page_index
                });
                
                const response = await window.axios.get(`/api/client/${this.client.url}/vectors?${params}`);
                this.vectors = response.data.vectors || [];
                this.local_num_vectors = response.data.total_count || 0;
                this.updatePagination();
            } catch (error) {
                console.error('Error fetching vectors:', error);
                this.vectors = [];
                this.local_num_vectors = 0;
            }
        },
        
        searchVectors() {
            this.page_index = 0;
            this.fetchVectors(this.selectedUsage);
        },
        
        sortByKey(key) {
            if (this.sort_key === key) {
                this.sort_order = this.sort_order === 'asc' ? 'des' : 'asc';
            } else {
                this.sort_key = key;
                this.sort_order = 'asc';
            }
            this.page_index = 0;
            this.fetchVectors(this.selectedUsage);
        },
        
        updatePagination() {
            this.page_count = Math.ceil(this.local_num_vectors / this.num_per_page);
            this.page_index_array = Array.from({length: this.page_count}, (_, i) => i + 1);
            this.getItemInterval();
        },
        
        getItemInterval() {
            if (this.local_num_vectors > 0) {
                var from = this.page_index * this.num_per_page + 1;
                var to = (this.page_index + 1) * this.num_per_page;
                if (this.local_num_vectors && to > this.local_num_vectors)
                    to = this.local_num_vectors;
                if (from >= to)
                    this.item_interval = to;
                else
                    this.item_interval = from + ' - ' + to;
            } else {
                this.item_interval = '0';
            }
        },
        
        moveToPrevPage() {
            this.page_index--;
            this.fetchVectors(this.selectedUsage);
        },
        
        moveToPage(page) {
            this.page_index = page - 1;
            this.fetchVectors(this.selectedUsage);
        },
        
        moveToNextPage() {
            this.page_index++;
            this.fetchVectors(this.selectedUsage);
        },
        
        reloadPagination() {
            this.updatePagination();
        },
        
        openUploadModal() {
            // Reset modal values
            this.modalUrlValue = '';
            this.modalFileValue = null;
            if (this.$refs.modalFileInput) {
                this.$refs.modalFileInput.value = '';
            }
            
            this.$nextTick(() => {
                const modalElement = document.getElementById('resource-upload-modal');
                if (modalElement) {
                    // Dispose any existing modal instance
                    const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                    if (existingModal) {
                        existingModal.dispose();
                    }
                    
                    // Create new modal instance
                    const modal = new window.bootstrap.Modal(modalElement, {
                        backdrop: 'static',
                        keyboard: false,
                        focus: true
                    });
                    
                    // Setup event listener to reset modal values when modal is closed
                    const handleHidden = () => {
                        this.closeUploadModal();
                        modalElement.removeEventListener('hidden.bs.modal', handleHidden);
                    };
                    modalElement.addEventListener('hidden.bs.modal', handleHidden);
                    
                    // Show the modal
                    modal.show();
                    
                    // Reinitialize dropzone after modal is shown
                    this.$nextTick(() => {
                        this.initializeDropzone();
                        
                        // Initialize tooltips in the modal
                        const tooltipTriggerList = modalElement.querySelectorAll('[data-bs-toggle="tooltip"]');
                        [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                            const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                            if (existingTooltip) {
                                existingTooltip.dispose();
                            }
                            new window.bootstrap.Tooltip(tooltipTriggerEl);
                        });
                    });
                }
            });
        },
        
        closeUploadModal() {
            // Reset modal values
            this.modalUrlValue = '';
            this.modalFileValue = null;
            if (this.$refs.modalFileInput) {
                this.$refs.modalFileInput.value = '';
            }
            
            // Close the modal
            const modalElement = document.getElementById('resource-upload-modal');
            if (modalElement) {
                const modal = window.bootstrap.Modal.getInstance(modalElement);
                if (modal) {
                    modal.hide();
                }
            }
        },
        
        handleUrlInput() {
            // Clear file when URL is entered
            if (this.modalUrlValue && this.modalUrlValue.trim().length > 0) {
                this.modalFileValue = null;
                if (this.$refs.modalFileInput) {
                    this.$refs.modalFileInput.value = '';
                }
            }
        },
        
        initializeDropzone() {
            const onDrop = async (acceptedFiles) => {
                // Only process if URL is not entered
                if (this.modalUrlValue && this.modalUrlValue.trim().length > 0) {
                    return;
                }
                
                // Only process if not already uploading
                if (this.uploading || this.processingUrl) {
                    return;
                }
                
                const file = acceptedFiles[0];
                if (file) {
                    // Validate file type
                    if (!this.isValidFileType(file)) {
                        this.showNotification({
                            text: 'Invalid file type. Please select a supported file type.',
                            type: 'error'
                        });
                        return;
                    }
                    
                    // Set file and upload immediately
                    this.modalFileValue = file;
                    this.uploadFile = file;
                    await this.uploadResource();
                }
            };

            const dropzoneOptions = {
                onDrop,
                multiple: false,
                maxFilesize: 50, // MB (matching the backend limit)
                disabled: this.uploading || this.processingUrl || (this.modalUrlValue && this.modalUrlValue.trim().length > 0)
            };

            const { getRootProps, getInputProps, isDragActive, open } = useDropzone(dropzoneOptions);
            
            this.getRootProps = getRootProps;
            this.getInputProps = getInputProps;
            this.isDragActive = isDragActive;
            this.openDropzone = open;
            
            // Setup click handler after modal is shown
            this.$nextTick(() => {
                if (this.$refs.dropzone && open) {
                    // Remove existing click listeners
                    const dropzoneEl = this.$refs.dropzone;
                    const clonedEl = dropzoneEl.cloneNode(true);
                    dropzoneEl.parentNode.replaceChild(clonedEl, dropzoneEl);
                    this.$refs.dropzone = clonedEl;
                    
                    // Add click listener to open file dialog
                    clonedEl.addEventListener('click', (e) => {
                        if (!this.uploading && !this.processingUrl && (!this.modalUrlValue || !this.modalUrlValue.trim())) {
                            open();
                        }
                    });
                }
            });
        },
        
        handleModalFileSelect(event) {
            const file = event.target.files[0];
            if (file) {
                // Validate file type
                if (!this.isValidFileType(file)) {
                    this.showNotification({
                        text: 'Invalid file type. Please select a supported file type.',
                        type: 'error'
                    });
                    // Clear the file input
                    if (this.$refs.modalFileInput) {
                        this.$refs.modalFileInput.value = '';
                    }
                    this.modalFileValue = null;
                    return;
                }
                this.modalFileValue = file;
                
                // Clear URL when file is selected
                this.modalUrlValue = '';
            }
        },
        
        isValidUrl(url) {
            if (!url || !url.trim()) {
                return false;
            }
            try {
                new URL(url.trim());
                return true;
            } catch {
                return false;
            }
        },
        
        async confirmUpload() {
            // If URL is provided, process URL
            if (this.modalUrlValue && this.modalUrlValue.trim()) {
                await this.processUrl();
            } 
            // If file is uploaded, process file
            else if (this.modalFileValue) {
                this.uploadFile = this.modalFileValue;
                this.closeUploadModal();
                await this.uploadResource();
            }
        },
        
        async processUrl() {
            if (!this.modalUrlValue || !this.modalUrlValue.trim()) {
                this.showNotification({
                    text: 'Please enter a valid URL.',
                    type: 'error'
                });
                return;
            }
            
            if (!this.isValidUrl(this.modalUrlValue)) {
                this.showNotification({
                    text: 'Please enter a valid URL.',
                    type: 'error'
                });
                return;
            }
            
            this.processingUrl = true;
            
            try {
                const response = await window.axios.post(`/api/client/${this.client.url}/vectors/url`, {
                    url: this.modalUrlValue.trim(),
                    usage: this.selectedUsage
                });
                
                if (response.data.success) {
                    // Close the modal
                    this.closeUploadModal();
                    
                    // Refresh the vectors list
                    await this.fetchVectors(this.selectedUsage);
                    
                    this.showNotification({
                        text: 'URL processing has started. The information will be added to your resource library shortly.'
                    });
                } else {
                    throw new Error(response.data.message || 'Failed to process URL');
                }
            } catch (error) {
                console.error('Error processing URL:', error);
                const errorMessage = error.response?.data?.message || 'Failed to process URL. Please try again.';
                this.showNotification({
                    text: errorMessage,
                    type: 'error'
                });
            } finally {
                this.processingUrl = false;
            }
        },
        
        triggerFileUpload() {
            this.$refs.fileInput.click();
        },
        
        handleFileSelect(event) {
            this.uploadFile = event.target.files[0];
            if (this.uploadFile) {
                // Validate file type
                if (!this.isValidFileType(this.uploadFile)) {
                    this.showNotification({
                        text: 'Invalid file type. Please select a supported file type.',
                        type: 'error'
                    });
                    // Clear the file input
                    this.$refs.fileInput.value = '';
                    this.uploadFile = null;
                    return;
                }
                this.uploadResource();
            }
        },
        
        isValidFileType(file) {
            const allowedExtensions = [
                '.css', '.doc', '.docx', '.html', 
                '.json', '.md', '.pdf', '.pptx', 
                 '.tex', '.ts', '.txt'
            ];
            
            const allowedMimeTypes = [
                'text/css',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'text/html',
                'text/javascript',
                'application/json',
                'text/markdown',
                'application/pdf',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                'text/plain'
            ];
            
            // Check file extension
            const fileName = file.name.toLowerCase();
            const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
            
            // Check MIME type
            const hasValidMimeType = allowedMimeTypes.includes(file.type);
            
            return hasValidExtension || hasValidMimeType;
        },
        
        async uploadResource() {
            if (!this.uploadFile) {
                this.showNotification({
                    text: 'Please select a file to upload.',
                    type: 'error'
                });
                return;
            }
            
            this.uploading = true;
            
            try {
                const formData = new FormData();
                formData.append('file', this.uploadFile);
                formData.append('usage', this.selectedUsage);
                
                const response = await window.axios.post(`/api/client/${this.client.url}/vectors`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                
                if (response.data.success) {
                    // Close the modal
                    this.closeUploadModal();
                    
                    // Refresh the vectors list
                    await this.fetchVectors(this.selectedUsage);
                                        
                    const fileName = this.uploadFile?.name || 'Your document';
                    this.showNotification({
                        text: '<strong>' + fileName + '</strong> was successfully uploaded!'
                    });

                }
            } catch (error) {
                console.error('Error uploading file:', error);
                const fileName = this.uploadFile?.name || 'your document';
                this.showNotification({
                    text: 'Failed to upload <strong>' + fileName + '</strong>. Please try again.',
                    type: 'error'
                });

            } finally {
                this.uploading = false;
                this.uploadFile = null;
                this.$refs.fileInput.value = '';
            }
        },
        
        async deleteVector(vectorId) {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: 'This will delete this resource. This action cannot be undone.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'Cancel',
                customClass: {
                    popup: 'swal-warning'
                },
            });
            
            if (result.isConfirmed) {
                try {
                    await window.axios.delete(`/api/client/${this.client.url}/vectors/${vectorId}`);
                    
                    // Find the vector name before deletion
                    const vectorName = this.vectors.find(v => v.id === vectorId)?.name || 'Your resource';
                    this.showNotification({
                        text: '<strong>' + vectorName + '</strong> was successfully deleted!'
                    });
                    
                    // Refresh the vectors list
                    await this.fetchVectors(this.selectedUsage);
                    
                } catch (error) {
                    console.error('Error deleting vector:', error);
                    const vectorName = this.vectors.find(v => v.id === vectorId)?.name || 'Your resource';
                    this.showNotification({
                        text: 'Failed to delete <strong>' + vectorName + '</strong>. Please try again.',
                        type: 'error'
                    });
                }
            }
        },
        
        capitalizeFirst(str) {
            return str.charAt(0).toUpperCase() + str.slice(1);
        },
        
        async handleVectorClick(vector) {
            if (vector.data_source === 'client_documents') {
                // For client documents, open the original document in a new window
                if (vector.link) {
                    window.open(vector.link, '_blank');
                }
            } else {
                // For other data sources, show markdown content in modal
                await this.showMarkdownContent(vector);
            }
        },
        
        async showMarkdownContent(vector) {
            this.modalTitle = vector.name;
            this.loadingContent = true;
            this.markdownContent = '';
            this.currentVector = vector;
            this.isEditMode = false;
            this.editedContent = '';
            
            try {
                const response = await window.axios.get(`/api/client/${this.client.url}/vectors/${vector.id}/content`);
                this.markdownContent = response.data.content || '';
                this.editedContent = this.markdownContent; // Initialize edited content
                
                // Show the modal
                const modal = new bootstrap.Modal(document.getElementById('markdownModal'));
                modal.show();
            } catch (error) {
                console.error('Error fetching markdown content:', error);
                this.markdownContent = '';
                this.showNotification({
                    text: 'Failed to load content. Please try again.',
                    type: 'error'
                });
            } finally {
                this.loadingContent = false;
            }
        },
        
        toggleEditMode() {
            this.isEditMode = true;
            this.editedContent = this.markdownContent;
        },
        
        extractTitleFromMarkdown(content) {
            // Look for the first H1 header (# Title) in the markdown
            const h1Match = content.match(/^#\s+(.+)$/m);
            if (h1Match) {
                return h1Match[1].trim();
            }
            
            // Look for the first H2 header (## Title) if no H1 found
            const h2Match = content.match(/^##\s+(.+)$/m);
            if (h2Match) {
                return h2Match[1].trim();
            }
            
            // If no headers found, return null
            return null;
        },
        
        async saveContent() {
            if (!this.currentVector || !this.editedContent.trim()) {
                this.showNotification({
                    text: 'Please add content to save your changes.',
                    type: 'error'
                });
                return;
            }
            
            this.saving = true;
            
            try {
                // Extract title from markdown content
                const extractedTitle = this.extractTitleFromMarkdown(this.editedContent);
                
                const response = await window.axios.put(`/api/client/${this.client.url}/vectors/${this.currentVector.id}/content`, {
                    content: this.editedContent,
                    title: extractedTitle
                });
                
                if (response.data.success) {
                    // Update the displayed content
                    this.markdownContent = this.editedContent;
                    this.isEditMode = false;
                    
                    // Refresh the vectors list to show updated name
                    await this.fetchVectors(this.selectedUsage);
                    
                    const vectorName = this.currentVector?.name || 'content';
                    this.showNotification({
                        text: '<strong>' + vectorName + '</strong> was successfully updated!'
                    });
                } else {
                    throw new Error(response.data.message || 'Failed to save content');
                }
            } catch (error) {
                console.error('Error saving content:', error);
                this.showNotification({
                    text: 'Failed to save your changes. Please try again.',
                    type: 'error'
                });
            } finally {
                this.saving = false;
            }
        },
        
        async triggerScrape() {
            this.scraping = true;
            
            try {
                const response = await window.axios.post(`/api/client/${this.client.url}/update-vector-stores`);
                
                if (response.data && response.data.message) {
                    this.showNotification({
                        text: "Your resource refresh has begun; it may take a few minutes to complete. You'll receive a notification when it's complete."
                    });
                    // Keep button disabled after successful response (200-299 status codes)
                    // Don't set scraping = false so button stays disabled
                } else {
                    throw new Error('Unexpected response format');
                }
            } catch (error) {
                console.error('Error triggering vector store update:', error);
                const errorMessage = error.response?.data?.message || 'There was an error initiating the update. Please try again.';
                this.showNotification({
                    text: errorMessage,
                    type: 'error'
                });
                // Only re-enable button on error
                this.scraping = false;
            }
        }
    },
    computed: {
        formattedMarkdown() {
            if (!this.markdownContent) return '';
            
            let content = this.markdownContent;
            
            // Ensure we have actual newlines (handle escaped newlines in JSON)
            // Axios should handle this automatically, but ensure it's a string with real newlines
            if (typeof content !== 'string') {
                content = String(content);
            }
            
            // Split content into sections (by headers)
            const sections = content.split(/\n(?=#{1,6} )/);
            let filteredContent = '';
            
            sections.forEach(section => {
                const trimmedSection = section.trim();
                if (!trimmedSection) return;
                
                // Check if this section contains the sections we want to show
                const sectionLower = trimmedSection.toLowerCase();
                
                // Match headers (#, ##, ###, etc.)
                const hasHeader = /^#{1,6}\s+/.test(trimmedSection);
                
                if (hasHeader || 
                    sectionLower.includes('summary') || 
                    sectionLower.includes('keywords') || 
                    sectionLower.includes('source') || 
                    sectionLower.includes('published')) {
                    
                    // Skip the "Content" section (but keep headers that contain "content")
                    if (sectionLower.includes('content') && !hasHeader) {
                        return;
                    }
                    
                    filteredContent += trimmedSection + '\n\n';
                }
            });
            
            // If filtering resulted in empty content, use the original content
            // This handles cases where the filtering logic might be too restrictive
            if (!filteredContent.trim() && content.trim()) {
                filteredContent = content;
            }
            
            // Convert markdown to HTML with enhanced formatting
            let html = filteredContent
                // Headers (must be done before other replacements)
                .replace(/^### (.*$)/gim, '<h3 class="h6 mb-1">$1</h3>')
                .replace(/^## (.*$)/gim, '<h2 class="h5 mb-1">$1</h2>')
                .replace(/^# (.*$)/gim, '<h1 class="h4 mb-2">$1</h1>')
                
                // Format section headers BEFORE bold formatting to avoid nested tags
                // Match patterns like "**Summary:**" or "Summary:" at start of line
                .replace(/\*\*(Summary|Keywords|Source|Platform):\*\*/gi, '<strong>$1:</strong>')
                .replace(/^(Summary|Keywords|Source|Platform):\s*/gim, '<strong>$1:</strong> ')

                // Format dates (look for patterns like "Published: 2024-01-15" or "Published: January 15, 2024")
                .replace(/Published:\s*([^\n]+)/gi, (match, dateStr) => {
                    try {
                        dateStr = dateStr.replace("**", "");
                        const result = filters.full_datetime(dateStr);
                        return `<strong>Published:</strong> ${result}`;
                    } catch (error) {
                        return match;
                    }
                })
                
                // Links - convert markdown links to clickable links that open in new window (before bold to avoid conflicts)
                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary">$2</a>')

                // Bold and italic (process remaining bold text that wasn't already handled)
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.*?)\*/g, '<em>$1</em>')

                // Convert plain URLs to clickable links (http://, https://, www.)
                // Only match URLs that are NOT already inside anchor tags or href attributes
                .replace(/(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?])/g, (match, url, offset, string) => {
                    // Check if this URL is already inside an href attribute or anchor tag
                    const before = string.substring(0, offset);
                    const after = string.substring(offset);

                    // Check if we're inside an href attribute
                    const hrefMatch = before.match(/href=["']([^"']*)$/);
                    if (hrefMatch) {
                        // We're inside an href attribute, don't convert
                        return match;
                    }

                    // Check if we're inside an anchor tag (between <a...> and </a>)
                    const lastOpenTag = before.lastIndexOf('<a');
                    const lastCloseTag = before.lastIndexOf('</a>');

                    if (lastOpenTag > lastCloseTag) {
                        // There's an open <a> tag, check if we're inside it
                        const tagContent = string.substring(lastOpenTag);
                        const tagEnd = tagContent.indexOf('>');
                        const nextCloseTag = tagContent.indexOf('</a>');

                        // If we're after the > of <a> but before </a>, we're in link content
                        if (tagEnd > 0 && nextCloseTag > 0 && offset - lastOpenTag > tagEnd && offset - lastOpenTag < nextCloseTag) {
                            return match; // Inside anchor tag content
                        }
                    }

                    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary">${url}</a>`;
                })

                // Handle line breaks - preserve paragraph structure
                .replace(/(<\/h[1-6]>)\s*\n\s*\n+/g, '$1<br>')
                .replace(/\n\s*\n\s*\n+/g, '<br><br>')
                .replace(/\n/g, '<br>');
            
            return html;
        }
    }
});
</script>

